// Supabase Overview - Dashboard style section showing Database, Users, Storage summaries
// Inspired by Lovable's Cloud panel design

import { useState, useEffect, useCallback } from 'react';
import {
    ChevronRight,
    Table2
} from 'lucide-react';
import { useSupabaseMCP } from '@/hooks/useSupabaseMCP';
import { ScrollArea } from '@/components/ui/scroll-area';
import { PxMark } from '@/components/PxMark';

interface SupabaseOverviewProps {
    pixelProjectId?: string;
    onNavigate?: (section: 'database' | 'users' | 'storage' | 'functions' | 'logs') => void;
}

interface TableInfo {
    name: string;
    rowCount?: number;
}

export function SupabaseOverview({ pixelProjectId, onNavigate }: SupabaseOverviewProps) {
    const { isConnected, getSchema, executeSql } = useSupabaseMCP(pixelProjectId);
    const [tables, setTables] = useState<TableInfo[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const loadData = useCallback(async () => {
        if (!isConnected) return;

        setIsLoading(true);
        try {
            // Get tables
            const schemaResult = await getSchema();
            if (schemaResult.success && schemaResult.data) {
                const columnsData = schemaResult.data as { table_name: string }[];
                const uniqueTables = [...new Set(columnsData.map(c => c.table_name))];

                // Get row counts for each table (limit to first 5)
                const tablesWithCounts: TableInfo[] = [];
                for (const tableName of uniqueTables.slice(0, 5)) {
                    try {
                        const countResult = await executeSql(`SELECT count(*) as count FROM "${tableName}"`);
                        const count = countResult.success && countResult.data?.[0]?.count
                            ? parseInt(countResult.data[0].count)
                            : undefined;
                        tablesWithCounts.push({ name: tableName, rowCount: count });
                    } catch {
                        tablesWithCounts.push({ name: tableName });
                    }
                }

                setTables(tablesWithCounts);
            }
        } catch (err) {
            console.error('Failed to load overview data:', err);
        } finally {
            setIsLoading(false);
        }
    }, [isConnected, getSchema, executeSql]);

    useEffect(() => {
        if (isConnected) {
            loadData();
        }
    }, [isConnected, loadData]);

    if (!isConnected) {
        return (
            <div className="flex h-full flex-col items-center justify-center p-8 text-center">
                <PxMark state="idle" size={24} className="mb-4" />
                <h3 className="mb-2 text-[16px] font-medium text-foreground">Supabase non connecté</h3>
                <p className="text-sm text-muted-foreground">
                    Connectez votre compte Supabase via le popover dans le header.
                </p>
            </div>
        );
    }

    return (
        <ScrollArea className="h-full">
            <div className="space-y-6 p-6">
                {/* Section Base de données */}
                <section className="overflow-hidden border border-border bg-card">
                    <div className="flex items-center justify-between border-b border-border px-5 py-4">
                        <div className="flex items-center gap-3">
                            <h2 className="text-[16px] font-medium text-foreground">Base de données</h2>
                            <span className="text-sm text-muted-foreground">
                                Voir les tables et éditer les données
                            </span>
                        </div>
                        <span className="font-mono text-[11px] text-faint">
                            {tables.length} tables
                        </span>
                    </div>

                    <div className="divide-y divide-border">
                        {isLoading ? (
                            <div className="flex items-center justify-center py-8">
                                <PxMark state="thinking" size={20} />
                            </div>
                        ) : tables.length === 0 ? (
                            <div className="px-5 py-4 text-sm text-muted-foreground">
                                Aucune table trouvée
                            </div>
                        ) : (
                            <>
                                {tables.map((table) => (
                                    <button
                                        key={table.name}
                                        onClick={() => onNavigate?.('database')}
                                        className="flex w-full items-center justify-between px-5 py-3 text-left transition-colors hover:bg-secondary"
                                    >
                                        <div className="flex items-center gap-3">
                                            <Table2 className="h-4 w-4 text-muted-foreground" />
                                            <span className="font-mono text-[13px] text-foreground">{table.name}</span>
                                        </div>
                                        {table.rowCount !== undefined && (
                                            <span className="font-mono text-[11px] text-muted-foreground">
                                                {table.rowCount.toLocaleString('fr-FR')} lignes
                                            </span>
                                        )}
                                    </button>
                                ))}
                                <button
                                    onClick={() => onNavigate?.('database')}
                                    className="flex w-full items-center justify-center gap-1 px-5 py-3 text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                                >
                                    Voir tout
                                    <ChevronRight className="h-4 w-4" />
                                </button>
                            </>
                        )}
                    </div>
                </section>

                {/* Section Utilisateurs - À venir */}
                <section className="overflow-hidden border border-border bg-card">
                    <div className="flex items-center justify-between border-b border-border px-5 py-4">
                        <div className="flex items-center gap-3">
                            <h2 className="text-[16px] font-medium text-foreground">Utilisateurs</h2>
                            <span className="text-sm text-muted-foreground">
                                Gérer l'authentification
                            </span>
                        </div>
                        <span className="bg-secondary px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.08em] text-faint">
                            Bientôt
                        </span>
                    </div>

                    <div className="px-5 py-4 text-sm text-muted-foreground">
                        La gestion des utilisateurs sera disponible prochainement.
                    </div>
                </section>

                {/* Section Stockage - À venir */}
                <section className="overflow-hidden border border-border bg-card">
                    <div className="flex items-center justify-between border-b border-border px-5 py-4">
                        <div className="flex items-center gap-3">
                            <h2 className="text-[16px] font-medium text-foreground">Stockage</h2>
                            <span className="text-sm text-muted-foreground">
                                Gérer les fichiers
                            </span>
                        </div>
                        <span className="bg-secondary px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.08em] text-faint">
                            Bientôt
                        </span>
                    </div>

                    <div className="px-5 py-4 text-sm text-muted-foreground">
                        La gestion des fichiers sera disponible prochainement.
                    </div>
                </section>
            </div>
        </ScrollArea>
    );
}

export default SupabaseOverview;
